import { FC, useEffect, useMemo, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { fetchSites, Site } from "../../../redux/slices/sitesSlice";
import { openSnackbar } from "../../../redux/slices/snackbarSlice";
import {
  RepresentantSites,
  saveRepresentantSites,
} from "../slices/representantSitesSlice";
import SitesMultiSelect, { resolveSites } from "./SitesMultiSelect";

interface RepresentantSitesDialogProps {
  open: boolean;
  representant: RepresentantSites | null;
  onClose: () => void;
}

const RepresentantSitesDialog: FC<RepresentantSitesDialogProps> = ({
  open,
  representant,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const direction = useAppSelector((state) => state.languageSlice.direction);
  const { sites, loading: sitesLoading } = useAppSelector(
    (state) => state.sitesSlice
  );
  const saving = useAppSelector(
    (state) => state.representantSitesSlice.saving
  );

  const [sitesVente, setSitesVente] = useState<Site[]>([]);
  const [sitesExpedition, setSitesExpedition] = useState<Site[]>([]);

  const allSites = useMemo(() => sites || [], [sites]);

  useEffect(() => {
    if (open && !sites && !sitesLoading) {
      dispatch(fetchSites());
    }
  }, [dispatch, open, sites, sitesLoading]);

  useEffect(() => {
    if (open && representant) {
      setSitesVente(resolveSites(representant.sitesVente, allSites));
      setSitesExpedition(resolveSites(representant.sitesExpedition, allSites));
    }
  }, [open, representant, allSites]);

  const handleSave = async () => {
    if (!representant) return;
    try {
      await dispatch(
        saveRepresentantSites({
          userId: representant.userId,
          sitesVente: sitesVente.map((site) => site.Code),
          sitesExpedition: sitesExpedition.map((site) => site.Code),
        })
      ).unwrap();
      dispatch(
        openSnackbar({
          message: t("administration.representantSites.saved"),
          type: "success",
          horizontal: direction === "rtl" ? "left" : "right",
        })
      );
      onClose();
    } catch (error) {
      dispatch(
        openSnackbar({
          message:
            typeof error === "string"
              ? error
              : t("administration.representantSites.saveError"),
          type: "error",
          horizontal: direction === "rtl" ? "left" : "right",
        })
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="md"
      dir={direction}
      aria-labelledby="representant-sites-dialog-title"
    >
      <DialogTitle id="representant-sites-dialog-title">
        {t("administration.representantSites.dialogTitle", {
          name: representant?.username ?? "",
        })}
      </DialogTitle>
      <DialogContent dividers>
        {representant?.businessId && (
          <Typography variant="body2" color="text.secondary">
            {t("administration.representantSites.columns.code")} :{" "}
            {representant.businessId}
          </Typography>
        )}
        <SitesMultiSelect
          id="sitesVente"
          label={t("administration.representantSites.salesSites")}
          options={allSites}
          value={sitesVente}
          loading={sitesLoading}
          onChange={setSitesVente}
        />
        <SitesMultiSelect
          id="sitesExpedition"
          label={t("administration.representantSites.expSites")}
          options={allSites}
          value={sitesExpedition}
          loading={sitesLoading}
          onChange={setSitesExpedition}
        />
      </DialogContent>
      <DialogActions>
        <Stack direction="row" spacing={1}>
          <Button
            onClick={onClose}
            disabled={saving}
            sx={{ color: "#fff", backgroundColor: "red", width: "fit-content" }}
          >
            {t("administration.representantSites.cancel")}
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || sitesLoading}
            variant="contained"
            color="primary"
            sx={{ width: "fit-content" }}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {t("administration.representantSites.save")}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default RepresentantSitesDialog;
